/**
 * Subscription API Client
 * 
 * Frontend API client for subscription management:
 * - getSubscriptionStatus()
 * - getReferralCode()
 * - getReferralStats()
 * - getCreditBalance()
 * - createPortalSession()
 * - cancelSubscription()
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

// Inline tier constants to avoid import issues
const SUBSCRIPTION_TIERS = {
  FREE: 1,
  PLUS: 2,
  PRO: 3,
  ADVISOR: 5,
  ADMIN: 99
};

import { apiRequest } from '@/utils/api';

function getSubscriptionTierName(tier: number): string {
  switch (tier) {
    case 1:
      return 'Free';
    case 2:
      return 'Wealth Builder';
    case 3:
      return 'Investor';
    case 5:
      return 'Advisor';
    case 99:
      return 'Admin';
    default:
      return 'Unknown';
  }
}

// Types
export interface SubscriptionStatus {
  subscription_status: string;
  tier: number;
  trial_start_date?: string;
  trial_end_date?: string;
  subscription_source?: string;
  stripe_customer_id?: string;
  access_ends_at?: string | null;
  cancellation_scheduled_at?: string | null;
  cancelled_at?: string | null;
  downgraded_at?: string | null;
  stripe_details?: {
    status: string;
    current_period_start: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
  };
  isActive?: boolean;
  isTrialing?: boolean;
  canUpgrade?: boolean;
  upgradeTier?: number;
  upgradeUrl?: string;
}

export interface ReferralStats {
  totalReferrals: number;
  successfulReferrals: number;
  totalCredits: number;
  monthsPerReferral: number;
  recentReferrals: Array<{
    id: string;
    referred_user_id: string;
    reward_granted: boolean;
    created_at: string;
    users?: {
      email: string;
      signup_date?: string;
    };
  }>;
  creditHistory: Array<{
    months: number;
    source: string;
    created_at: string;
  }>;
}

export interface CreditSummary {
  availableCredits: number;
  totalEarned: number;
  totalConsumed: number;
  sourceBreakdown: {
    [key: string]: number;
  };
  history?: {
    additions: Array<{
      id: string;
      months: number;
      source: string;
      metadata?: object;
      created_at: string;
    }>;
    consumption: Array<{
      id: string;
      months_consumed: number;
      reason: string;
      created_at: string;
    }>;
  };
}

export interface PortalSession {
  url: string;
}

export interface CancelSubscriptionResult {
  success: boolean;
  message: string;
  subscription?: {
    status: string;
    cancel_at_period_end: boolean;
  };
}

// API client
export class SubscriptionAPI {

  /**
   * Get current user's subscription status.
   * Falls back to FREE tier only on a 401, i.e. when the server says the
   * caller is not authenticated - not merely because no token is visible to JS.
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      // No pre-flight token check: the session rides an HttpOnly cookie the
      // client cannot read, so "no token" is not something we can (or should)
      // detect here. Ask the server and let the 401 branch below handle a
      // genuinely unauthenticated caller.
      return await apiRequest<SubscriptionStatus>('GET', '/stripe/status');
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Authentication failed - return default FREE tier instead of throwing
        // This prevents auth errors from breaking the UI when auth state is transitioning
        console.warn('[Subscription] 401 received, returning default FREE tier');
        return { subscription_status: 'active', tier: SUBSCRIPTION_TIERS.FREE };
      }
      throw error;
    }
  }

  /**
   * Get user's referral code and stats
   */
  async getReferralCode(): Promise<{
    code: string;
    totalReferrals: number;
    successfulReferrals: number;
    totalCredits: number;
  }> {
    return apiRequest('GET', '/referral/code');
  }

  /**
   * Get detailed referral statistics
   */
  async getReferralStats(): Promise<ReferralStats> {
    return apiRequest('GET', '/referral/stats');
  }

  /**
   * Get credit balance and history
   */
  async getCreditBalance(): Promise<CreditSummary> {
    return apiRequest('GET', '/referral/credits');
  }

  /**
   * Create Stripe customer portal session
   */
  async createPortalSession(returnUrl?: string): Promise<PortalSession> {
    return apiRequest('POST', '/stripe/portal', {
      returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.href : ''),
    });
  }

  /**
   * Cancel current subscription
   */
  async cancelSubscription(immediately = false): Promise<CancelSubscriptionResult> {
    return apiRequest('POST', '/stripe/cancel', { immediately });
  }

  /**
   * Apply a referral code (during signup or anytime)
   */
  async applyReferralCode(code: string): Promise<{
    success: boolean;
    message: string;
    referrerId?: string;
  }> {
    return apiRequest('POST', '/referral/apply', { code });
  }

  /**
   * Validate a referral code
   */
  async validateReferralCode(code: string): Promise<{
    valid: boolean;
    referrerInitial?: string;
    error?: string;
  }> {
    try {
      return await apiRequest('POST', '/referral/validate', { code });
    } catch (error: any) {
      if (error.response?.data?.message) {
        return { valid: false, error: error.response.data.message };
      }
      return { valid: false, error: 'Validation failed' };
    }
  }

  /**
   * Get referrer info (who referred current user)
   */
  async getReferrerInfo(): Promise<{
    referred: boolean;
    referrer?: {
      code: string;
      emailInitial: string;
    };
  }> {
    return apiRequest('GET', '/referral/referrer');
  }

  /**
   * Get formatted subscription display data
   */
  async getSubscriptionDisplay(): Promise<{
    tier: number;
    tierName: string;
    status: string;
    statusDisplay: string;
    trialDaysRemaining?: number;
    credits?: number;
    isActive: boolean;
    isCancelling: boolean;
    isTrialing: boolean;
    canUpgrade: boolean;
    upgradeTier?: number;
    upgradeUrl?: string;
  }> {
    const status = await this.getSubscriptionStatus();

    const tierName = getSubscriptionTierName(status.tier);
    
    const isTrialing = status.subscription_status === 'trialing' && !!status.trial_end_date;
    const trialDaysRemaining = isTrialing
      ? Math.max(0, Math.ceil((new Date(status.trial_end_date!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : 0;

    const credits = await this.getCreditBalance()
      .catch((e) => {
        console.error('Failed to get credit balance:', e);
        return { availableCredits: 0 };
      })
      .then(r => r.availableCredits);

    const isActive = ['active', 'trialing'].includes(status.subscription_status);
    const isCancelling = status.subscription_status === 'cancelling';
    const isActiveOrCancelling = isActive || isCancelling;
    
    const canUpgrade = status.tier < SUBSCRIPTION_TIERS.PRO;
    const upgradeTier = canUpgrade ? SUBSCRIPTION_TIERS.PRO : undefined;
    const upgradeUrl = canUpgrade 
      ? `/subscription?upgrade=${upgradeTier === SUBSCRIPTION_TIERS.PRO ? 'investor' : 'builder'}`
      : undefined;

    let statusDisplay = status.subscription_status;
    switch (status.subscription_status) {
      case 'trialing':
        statusDisplay = trialDaysRemaining > 0 ? `Trial: ${trialDaysRemaining} days left` : 'Trial';
        break;
      case 'active':
        statusDisplay = 'Active';
        break;
      case 'cancelling':
        statusDisplay = `Cancelling${status.access_ends_at ? ` - ends ${new Date(status.access_ends_at).toLocaleDateString('en-GB')}` : ''}`;
        break;
      case 'past_due':
        statusDisplay = 'Payment Required';
        break;
      case 'canceled':
        statusDisplay = 'Canceled';
        break;
      case 'trial_expired':
        statusDisplay = 'Trial Expired';
        break;
      default:
        statusDisplay = status.subscription_status || 'Free';
    }

    return {
      tier: status.tier,
      tierName,
      status: status.subscription_status,
      statusDisplay,
      trialDaysRemaining: isTrialing ? trialDaysRemaining : undefined,
      credits: credits > 0 ? credits : undefined,
      isActive: isActiveOrCancelling,
      isCancelling,
      isTrialing,
      canUpgrade,
      upgradeTier,
      upgradeUrl
    };
  }
}

// Export singleton instance
export const subscriptionAPI = new SubscriptionAPI();

// Export types
export { SUBSCRIPTION_TIERS, getSubscriptionTierName };
